import HeaderClock from './HeaderClock';
import './ShowcaseHeader.css';

interface ShowcaseHeaderProps {
  /** Organization logo URL */
  logoUrl: string | null;
  /** Organization name (used as alt text) */
  orgName: string;
  /** Center content — event/circuit info or custom content */
  title?: string;
  subtitle?: string;
  /** Optional country code for flag (e.g. 'US', 'GB') */
  countryCode?: string | null;
  /** Event timezone — when provided, renders a live local clock in the right slot */
  timezone?: string | null;
  /** Optional right slot — overrides the default clock if provided */
  rightSlot?: React.ReactNode;
}

/**
 * ShowcaseHeader — Glassmorphic top bar shared across all showcase screens.
 *
 * Rendered once by ScreenRenderer (ShowcasePlayer.tsx) so every screen gets
 * it automatically. Individual screens should NOT render this themselves.
 *
 * Layout: [Logo]  [Title / Subtitle]  [Right slot | Clock]
 */
export default function ShowcaseHeader({
  logoUrl,
  orgName,
  title,
  subtitle,
  countryCode,
  timezone,
  rightSlot,
}: ShowcaseHeaderProps) {
  // Build flag URL from country code (using flagcdn)
  const flagUrl = countryCode
    ? `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`
    : null;

  // Default right slot: live local clock when timezone is available
  const right = rightSlot ?? (timezone ? <HeaderClock timezone={timezone} /> : null);

  return (
    <header className="sc-header">
      {/* ── Left: Logo ─────────────────────────────── */}
      <div className="sc-header__logo">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={orgName}
            className="sc-header__logo-img"
            draggable={false}
          />
        ) : (
          <div className="sc-header__logo-placeholder">
            {orgName.charAt(0)}
          </div>
        )}
      </div>

      {/* ── Center: Title + Subtitle ───────────────── */}
      {(title || subtitle) && (
        <div className="sc-header__center">
          {title && (
            <div className="sc-header__title">{title}</div>
          )}
          {subtitle && (
            <div className="sc-header__subtitle">
              {flagUrl && (
                <img
                  src={flagUrl}
                  alt={countryCode ?? ''}
                  className="loc-flag"
                />
              )}
              {subtitle}
            </div>
          )}
        </div>
      )}

      {/* ── Right: Clock (default) or custom slot ──── */}
      {right && (
        <div className="sc-header__right">
          {right}
        </div>
      )}
    </header>
  );
}
